import React, { createContext, useContext, useEffect, useState } from "react";
import { collection, getDocs, query, where, doc, getDoc, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";

const EmployeeAuthContext = createContext(null);

export const EmployeeAuthProvider = ({ children }) => {
  const [auth, setAuth] = useState(() => {
    const saved = localStorage.getItem("employeeAuth");
    return saved
      ? JSON.parse(saved)
      : { isAuthenticated: false, user: null, token: null };
  });

  const fetchEmployeeData = async (email) => {
    const q = query(collection(db, "employees"), where("email", "==", email));
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      const data = snapshot.docs[0].data();
      return data;
    }
    throw new Error("Employee not found in database");
  };

  //  const login = async (email, password) => {
  //    // Temporary HR + employee credentials
  //    const DEMO = [
  //      { email: "ceo@driveeasy.co.in", password: "DRVEMP#1605" },
  //      { email: "kamleshsharma@driveeasy.co.in", password: "DRVEMP#1605" },
  //      { email: "kanishka@driveeasy.co.in", password: "DRVHR#1605" },
  //    ];
  //
  //    const found = DEMO.find((u) => u.email === email && u.password === password);
  //    if (!found) throw new Error("Invalid credentials");
  //
  //    const employeeData = await fetchEmployeeData(email);
  //
  //    setAuth({
  //      isAuthenticated: true,
  //      user: employeeData,
  //      token: "employee-demo-token",
  //    });
  //  };

  // src/employee/auth/EmployeeAuthContext.js

  // Real-time listener for user profile updates (e.g., permissions changed by HR)
  useEffect(() => {
    let unsubscribe;

    if (auth.isAuthenticated && auth.user?.email) {
      const email = auth.user.email;
      const userRef = doc(db, "employees", email);

      console.log("Setting up real-time listener for:", email);

      unsubscribe = onSnapshot(userRef, (snapshot) => {
        if (snapshot.exists()) {
          const freshData = snapshot.data();
          // Only update if data actually changed to avoid unnecessary renders
          setAuth((prev) => {
            // quick deep compare check or just update. 
            // Simplest is to just update, React handles reconciling.
            // But we need to preserve the token and isAuthenticated.
            return {
              ...prev,
              user: { ...prev.user, ...freshData }
            };
          });
        }
      }, (error) => {
        console.error("Auth listener error:", error);
      });
    }

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [auth.isAuthenticated, auth.user?.email]);

  const login = async (email, password) => {
    try {
      // 1. Fetch employee doc from Firestore to validate first
      const employeeRef = doc(db, "employees", email);
      const employeeSnap = await getDoc(employeeRef);

      if (!employeeSnap.exists()) {
        throw new Error("Employee not found");
      }

      const employeeData = employeeSnap.data();

      // 2. Check password from Firestore (encrypted or plain – we use plain for now)
      if (employeeData.password !== password) {
        throw new Error("Invalid password");
      }

      // 3. Success → save to auth
      // The useEffect above will 'take over' and keep it fresh, 
      // but we set initial state here to allow immediate navigation.
      setAuth({
        isAuthenticated: true,
        user: { ...employeeData, email },
        token: "employee-token",
      });

    } catch (err) {
      throw new Error(err.message || "Login failed");
    }
  };

  const logout = () =>
    setAuth({ isAuthenticated: false, user: null, token: null });

  useEffect(() => {
    localStorage.setItem("employeeAuth", JSON.stringify(auth));
  }, [auth]);

  return (
    <EmployeeAuthContext.Provider value={{ ...auth, login, logout, setAuth }}>
      {children}
    </EmployeeAuthContext.Provider>
  );
};

export const useEmployeeAuth = () => useContext(EmployeeAuthContext);
